// Optimistic client-side state for the review controls. The server is the
// source of truth (POST /api/outputs/:id/rate and /choose); this reducer just
// lets the thumbs / choose highlight flip the instant a reviewer clicks,
// before the round trip lands. Pure - no React, no fetch - so it unit-tests
// on its own (web/test/ratingState.test.js).

import type { Rating } from "./types.js";

export interface OutputRatingState {
  outputId: string;
  rating: Rating | null;
  comment: string;
  chosen: boolean;
  chosenReason?: string;
}

export type RatingAction =
  | { type: "hydrate"; outputs: OutputRatingState[] }
  | { type: "rate"; outputId: string; rating: Rating }
  | { type: "comment"; outputId: string; comment: string }
  | { type: "choose"; outputId: string; reason?: string };

/** What a thumb click resolves to: clicking the already-active thumb clears it
 *  back to unrated, clicking the other thumb switches. */
export function nextRating(current: Rating | null, clicked: Rating): Rating | null {
  return current === clicked ? null : clicked;
}

export function initRatingState(
  outputs: Array<{
    outputId: string;
    rating?: Rating | null;
    ratingComment?: string;
    chosen?: boolean;
    chosenReason?: string;
  }>,
): OutputRatingState[] {
  return outputs.map((o) => ({
    outputId: o.outputId,
    rating: o.rating ?? null,
    comment: o.ratingComment ?? "",
    chosen: o.chosen ?? false,
    chosenReason: o.chosenReason,
  }));
}

export function ratingReducer(
  state: OutputRatingState[],
  action: RatingAction,
): OutputRatingState[] {
  switch (action.type) {
    case "hydrate":
      return action.outputs;
    case "rate":
      return state.map((o) =>
        o.outputId === action.outputId
          ? { ...o, rating: nextRating(o.rating, action.rating) }
          : o,
      );
    case "comment":
      return state.map((o) =>
        o.outputId === action.outputId ? { ...o, comment: action.comment } : o,
      );
    case "choose":
      // D-9: exactly one output of a deliverable is "chosen"; picking one
      // clears every sibling in the same list.
      return state.map((o) =>
        o.outputId === action.outputId
          ? { ...o, chosen: true, chosenReason: action.reason || undefined }
          : { ...o, chosen: false, chosenReason: undefined },
      );
    default:
      return state;
  }
}

export function ratingStateFor(
  state: OutputRatingState[],
  outputId: string,
): OutputRatingState {
  return (
    state.find((o) => o.outputId === outputId) ?? {
      outputId,
      rating: null,
      comment: "",
      chosen: false,
    }
  );
}
